// --------------------------------------------------------------
// Created On: 2025-07-22
// Author: Dimitra Weinstein
//
// Last Modified: 2025-10-22
// Modified By: Dimitra Weinstein
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useState } from "react";
import { API } from "src/constants/miscellaneous";
import { CROSS_COMPANY_PERMISSION } from "src/constants/permissions";
import useApi from "src/hooks/useApi";
import Spinner from "src/components/Spinner/Spinner";
import AssetBulkConfigurationCompanyList from "./AssetBulkConfigurationCompanyList/AssetBulkConfigurationCompanyList";
import userHasPermission from "src/utilities/userHasPermission";
import Error500Page from "../Error500Page/Error500Page";

// Page for super admins to bulk configure assets across multiple companies.
export default function SuperAdminAssetBulkConfigurationPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Get company data from API.
  useApi(
    () => {
      if (userHasPermission([[CROSS_COMPANY_PERMISSION]])) {
        setLoading(true);
        return true;
      } else {
        setLoading(false);
        return false;
      }
    },
    {
      url: `${API}/company`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setCompanies(responseBody.companies);
        setFailedToLoad(false);
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    []
  );

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className="p-4">
      <Spinner loading={loading} />
      <div className="row">
        <AssetBulkConfigurationCompanyList companies={companies} />
      </div>
    </div>
  );
}

interface ResponseBody {
  companies: Company[];
}

interface Company {
  companyId: number;
  name: string;
  code: string;
  isPackager: boolean;
}
